import { defaultRangeExtractor, type Range, useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useMemo, useRef } from "react";
import { buttonVariants } from "@/components/ui/button";
import { ICON_KEYS, iconRegistry } from "@/icons";
import type { PickerGroup } from "./group-picker-streams";
import { type PickerRowText, pickerRowTexts } from "./picker-row-text";
import { GroupHeader, ROW_CLASS, RowText, useRowLabel } from "./picker-rows";
import type { PickerStream } from "./picker-stream";

const PlusIcon = iconRegistry[ICON_KEYS.PLUS];

type AvailableItem =
    | { kind: "group"; key: string; label: string; count: number }
    | {
          kind: "row";
          key: string;
          stream: PickerStream;
          text: PickerRowText;
          group: string;
          firstInGroup: boolean;
          lastInGroup: boolean;
      };

// Starting guesses only; each item is measured once it renders.
const ESTIMATED_HEIGHT: Record<AvailableItem["kind"], number> = { group: 26, row: 32 };

function toAvailableItems(groups: readonly PickerGroup[]): AvailableItem[] {
    return groups.flatMap((group) => {
        const texts = pickerRowTexts(group.streams);
        return [
            { kind: "group" as const, key: `group:${group.key}`, label: group.label, count: group.streams.length },
            ...group.streams.map((stream, index) => ({
                kind: "row" as const,
                key: `row:${stream.id}`,
                stream,
                text: texts.get(stream.id) ?? { measurement: stream.name, detail: "" },
                group: group.label,
                firstInGroup: index === 0,
                lastInGroup: index === group.streams.length - 1,
            })),
        ];
    });
}

/** Only the rows on screen render, so a big org opens and searches without redrawing thousands of rows. */
export function AvailableList({
    groups,
    showVisibility,
    onAdd,
}: {
    groups: readonly PickerGroup[];
    showVisibility: boolean;
    onAdd: (streamId: number) => void;
}) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const items = useMemo(() => toAvailableItems(groups), [groups]);
    const groupIndexes = useMemo(() => items.flatMap((item, index) => (item.kind === "group" ? [index] : [])), [items]);

    // The zone header the list is scrolled into stays rendered and pinned, the way the old sticky header did.
    const pinnedIndexRef = useRef<number | null>(null);
    const rangeExtractor = useCallback(
        (range: Range) => {
            const pinned = [...groupIndexes].reverse().find((index) => index <= range.startIndex) ?? null;
            pinnedIndexRef.current = pinned;
            const indexes = defaultRangeExtractor(range);
            return pinned === null || indexes.includes(pinned) ? indexes : [pinned, ...indexes];
        },
        [groupIndexes],
    );

    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: (index) => ESTIMATED_HEIGHT[items[index]?.kind ?? "row"],
        getItemKey: (index) => items[index]?.key ?? index,
        overscan: 10,
        rangeExtractor,
    });

    return (
        <div ref={scrollRef} className={"min-h-0 flex-1 overflow-y-auto"}>
            <div className={"relative w-full"} style={{ height: virtualizer.getTotalSize() }}>
                {virtualizer.getVirtualItems().map((virtualItem) => {
                    const item = items[virtualItem.index];
                    if (!item) return null;
                    const pinned = pinnedIndexRef.current === virtualItem.index;
                    return (
                        <div
                            key={virtualItem.key}
                            ref={virtualizer.measureElement}
                            data-index={virtualItem.index}
                            className={pinned ? "sticky top-0 z-[1]" : "absolute top-0 left-0 w-full"}
                            style={pinned ? undefined : { transform: `translateY(${virtualItem.start}px)` }}
                        >
                            <AvailableListItem item={item} showVisibility={showVisibility} onAdd={onAdd} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function AvailableListItem({
    item,
    showVisibility,
    onAdd,
}: {
    item: AvailableItem;
    showVisibility: boolean;
    onAdd: (streamId: number) => void;
}) {
    switch (item.kind) {
        case "group":
            return <GroupHeader label={item.label} count={item.count} />;
        case "row":
            return (
                <div className={`${item.firstInGroup ? "pt-1" : ""} ${item.lastInGroup ? "pb-1.5" : ""}`}>
                    <AvailableRow
                        stream={item.stream}
                        text={item.text}
                        group={item.group}
                        showVisibility={showVisibility}
                        onAdd={onAdd}
                    />
                </div>
            );
        default:
            return null;
    }
}

function AvailableRow({
    stream,
    text,
    group,
    showVisibility,
    onAdd,
}: {
    stream: PickerStream;
    text: PickerRowText;
    group: string;
    showVisibility: boolean;
    onAdd: (streamId: number) => void;
}) {
    const rowLabel = useRowLabel();
    return (
        <button
            type={"button"}
            aria-label={rowLabel("add", stream, text, group)}
            className={`${ROW_CLASS} cursor-pointer hover:bg-accent`}
            onClick={() => onAdd(stream.id)}
        >
            <RowText stream={stream} text={text} showVisibility={showVisibility} />
            {/* Looks like a button, but the whole row is the button. */}
            <span aria-hidden className={buttonVariants({ size: "icon-xs", variant: "outline" })}>
                <PlusIcon className={"size-3"} />
            </span>
        </button>
    );
}
